import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import walletApi from '../../api/wallet';
import { formatCurrency } from '../../utils/Currency';
import { LAYOUT } from '../../constants/layout';
import KPICards from './KPICards';
import QuickActions from './QuickActions';
import DistributionChart from './DistributionChart';
import PortfolioSummary from '../portfolio/PortfolioSummary';
import HomeChart from './HomeChart';
import { useCurrency } from '../../contexts/CurrencyContext';

const SummaryCard = ({ title, value, subtext, color, actions, showValues }) => {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        borderTop: `4px solid ${color}`,
        boxShadow: theme.shadows[2],
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color }}
        >
          {showValues ? value : '--'}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, mb: 2 }}
      >
        {showValues ? subtext : ''}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
        {actions.map((action, index) => (
          <Box
            key={index}
            onClick={action.onClick}
            sx={{
              flex: 1,
              p: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(color, 0.08),
                borderColor: color,
              },
            }}
          >
            <Typography variant="body2">{action.label}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const HomeNew = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const [showValues, setShowValues] = useState(false);
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState(null);
  const [positions, setPositions] = useState([]);
  const [cashInTotal, setCashInTotal] = useState(0);
  const [cashOutTotal, setCashOutTotal] = useState(0);
  const [cashInCount, setCashInCount] = useState(0);
  const [cashOutCount, setCashOutCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const [summaryResponse, positionsResponse] = await Promise.all([
          walletApi.get('/home/summary?days=31'),
          walletApi.get('/positions/asset-class'),
        ]);

        if (summaryResponse.status === 200 && summaryResponse.data) {
          const { CashIn, CashOut, Positions } = summaryResponse.data;

          setCashInTotal(CashIn?.Total?.[currency] || 0);
          setCashInCount(CashIn?.Count || 0);
          setCashOutTotal(CashOut?.Total?.[currency] || 0);
          setCashOutCount(CashOut?.Count || 0);

          setKpiData({
            totalInvested: Positions?.TotalInvested?.[currency] || 0,
            currentValue: Positions?.CurrentValue?.[currency] || 0,
            assetCount: Positions?.AssetCount || 0,
            monthlyIncome: 0,
          });
        }

        if (positionsResponse.status === 200 && positionsResponse.data) {
          const individualPositions = positionsResponse.data.IndividualPositions || [];
          setPositions(individualPositions);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currency]);

  const summaryCards = [
    {
      title: 'Receitas',
      value: formatCurrency(currency, cashInTotal),
      subtext: `Ultimos 31 dias - ${cashInCount} lancamentos`,
      color: theme.palette.success.main,
      actions: [
        { label: 'Ver Lista', onClick: () => navigate('/cashin/list') },
        { label: 'Graficos', onClick: () => navigate('/cashin/dashboard') },
      ],
    },
    {
      title: 'Custos',
      value: formatCurrency(currency, cashOutTotal),
      subtext: `Ultimos 31 dias - ${cashOutCount} lancamentos`,
      color: theme.palette.error.main,
      actions: [
        { label: 'Ver Lista', onClick: () => navigate('/cashout/list') },
        { label: 'Graficos', onClick: () => navigate('/cashout/dashboard') },
      ],
    },
    {
      title: 'Investimentos',
      value: formatCurrency(currency, kpiData?.currentValue || 0),
      subtext: `${kpiData?.assetCount || 0} ativos`,
      color: theme.palette.info.main,
      actions: [
        { label: 'Operacoes', onClick: () => navigate('/operations/list') },
        { label: 'Posicoes', onClick: () => navigate('/positions') },
      ],
    },
  ];

  return (
    <Container maxWidth={false} disableGutters sx={LAYOUT.PAGE_SPACING}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        <Tooltip title={showValues ? 'Ocultar valores' : 'Mostrar valores'}>
          <IconButton
            onClick={() => setShowValues(!showValues)}
            size="small"
            aria-label={showValues ? 'Ocultar valores' : 'Mostrar valores'}
          >
            {showValues ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
        </Tooltip>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Resumo do Patrimonio
        </Typography>
      </Box>

      <KPICards
        data={kpiData}
        currency={currency}
        loading={loading}
        showValues={showValues}
      />

      <QuickActions />

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} xl={9}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: theme.shadows[2],
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
              Evolucao Patrimonial
            </Typography>
            <Box sx={{ height: { xs: 360, md: 450 } }}>
              <HomeChart embedded showValues={showValues} currency={currency} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} xl={3}>
          <DistributionChart
            positions={positions}
            currency={currency}
            loading={loading}
            showValues={showValues}
          />
        </Grid>
      </Grid>

      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 1,
          color: theme.palette.text.secondary,
          mb: 1.5,
          display: 'block',
        }}
      >
        Visao Geral
      </Typography>
      <Grid container spacing={2}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} md={4} key={index}>
            <SummaryCard {...card} showValues={showValues} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3 }}>
        <PortfolioSummary />
      </Box>
    </Container>
  );
};

export default HomeNew;
